/**
 * Row rules for sheets the browser parses itself (Teachers and Students spreadsheets with a
 * recognisable header never reach the server). They mirror the server's checks, so a row is
 * left out for the same reason whichever side read the file, with the same code:
 *
 *  - `NOT_A_NAME`       the name has no letter but a digit ("50", a total row);
 *  - `NAME_SYMBOLS_ONLY` the name has neither a letter nor a digit ("???", "@@##$$");
 *  - `NAME_HAS_DIGITS`  the name has a digit;
 *  - `NAME_NOT_ENGLISH` the save would drop some of its letters (Urdu, Arabic…): after NFD and
 *                       removing combining marks, anything above U+00FF is lost. `Zoë` passes;
 *  - `NOT_A_RECORD`     a heading or note inside the table ("--- New starters ---"). Teachers
 *                       only: a student row with nothing but a name is kept for the grid.
 *
 * Pure: no React, no I/O.
 */
import type {DataUploadRowCode, DataUploadSkippedRow} from 'common/helpers/dataUpload'

export type NameRuleCode = Exclude<DataUploadRowCode, 'NOT_A_RECORD'>

const HAS_LETTER = /\p{L}/u
const HAS_DIGIT = /\p{N}/u
const COMBINING_MARKS = /\p{M}/gu

/** Why `name` can't be saved as typed, or null when it can. */
export const nameRuleViolation = (name: string): NameRuleCode | null => {
  if (!HAS_LETTER.test(name)) return HAS_DIGIT.test(name) ? 'NOT_A_NAME' : 'NAME_SYMBOLS_ONLY'
  if (HAS_DIGIT.test(name)) return 'NAME_HAS_DIGITS'
  const stripped = name.normalize('NFD').replace(COMBINING_MARKS, '')
  for (const char of stripped) {
    if ((char.codePointAt(0) ?? 0) > 0xff) return 'NAME_NOT_ENGLISH'
  }
  return null
}

const isFilled = (cell: string | undefined) => String(cell ?? '').trim().length > 0

/**
 * Indexes (into `rows`) of lines that fill only the name cell(s) in a table where some other
 * column is filled on nearly every row: all but `max(2, 10%)` of them, as on the server. Such a
 * line is a heading or a note ("--- New starters this term ---"), not a person.
 *
 * @param rows Data rows only (no header), blank rows included or not.
 * @param nameColumns The column(s) holding the name.
 */
export const notARecordRows = (
  rows: readonly (readonly string[])[],
  nameColumns: readonly number[]
): Set<number> => {
  const flagged = new Set<number>()
  const dataRows = rows
    .map((cells, index) => ({cells, index}))
    .filter(({cells}) => cells.some(isFilled))
  const total = dataRows.length
  if (total === 0) return flagged

  const width = Math.max(...dataRows.map(({cells}) => cells.length))
  const nameSet = new Set(nameColumns)
  const tolerance = Math.max(2, Math.floor(total * 0.1))
  let hasDenseColumn = false
  for (let col = 0; col < width && !hasDenseColumn; col++) {
    if (nameSet.has(col)) continue
    const filled = dataRows.filter(({cells}) => isFilled(cells[col])).length
    // "More filled than empty" keeps a 3-row sheet from calling two rows notes.
    hasDenseColumn = filled >= total - tolerance && filled > total - filled
  }
  if (!hasDenseColumn) return flagged

  for (const {cells, index} of dataRows) {
    const onlyName = cells.every((cell, col) => nameSet.has(col) || !isFilled(cell))
    if (onlyName) flagged.add(index)
  }
  return flagged
}

/** A left-out row in the shape the server's `report.skipped` uses. */
export const skippedRow = (label: string, code: DataUploadRowCode): DataUploadSkippedRow => ({
  label,
  code,
  reason: code,
})
